import { useEffect, useState } from "react";
import { AlertTriangle, Ban, CheckCircle2, Info } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Central hook — one fetch, cached at module level to avoid hammering the
 * endpoint from every card that references it. Refetch on window focus so a
 * newly-generated I10 report shows up without a manual refresh.
 */
let _cache = null;
let _inflight = null;
const _subs = new Set();

async function _fetch() {
  if (_inflight) return _inflight;
  _inflight = api.get("/validation/status")
    .then((r) => { _cache = r.data; _subs.forEach((fn) => fn(_cache)); return _cache; })
    .catch(() => { _cache = { intraday: null, swing: null }; _subs.forEach((fn) => fn(_cache)); return _cache; })
    .finally(() => { _inflight = null; });
  return _inflight;
}

export function useValidationStatus() {
  const [data, setData] = useState(_cache);
  useEffect(() => {
    const fn = (d) => setData(d);
    _subs.add(fn);
    if (!_cache) _fetch();
    else setData(_cache);
    return () => { _subs.delete(fn); };
  }, []);
  return data;
}

/** Chip: compact "Validation: NULL" / VALIDATED / MARGINAL / UNKNOWN. */
export function ValidationChip({ status, testid, className = "" }) {
  if (!status || !status.verdict) return null;
  const v = status.verdict;
  const map = {
    VALIDATED: { cls: "border-emerald-500/60 bg-emerald-500/10 text-emerald-200", Icon: CheckCircle2, label: "Validated" },
    MARGINAL:  { cls: "border-amber-500/60 bg-amber-500/10 text-amber-200", Icon: AlertTriangle, label: "Marginal — Descriptive" },
    NULL:      { cls: "border-slate-500/60 bg-slate-500/10 text-slate-200", Icon: Ban, label: "Not Validated — Descriptive" },
    UNKNOWN:   { cls: "border-slate-700 bg-slate-800/40 text-slate-400", Icon: Info, label: "Not Yet Validated" },
  };
  const meta = map[v] || map.UNKNOWN;
  const Icon = meta.Icon;
  return (
    <span
      data-testid={testid || `validation-chip-${status.engine || "unknown"}`}
      data-verdict={v}
      title={status.human || ""}
      className={`inline-flex items-center gap-1 mono text-[10px] uppercase tracking-widest px-1.5 py-0.5 border ${meta.cls} ${className}`}
    >
      <Icon className="h-2.5 w-2.5" />
      {meta.label}
    </span>
  );
}

/** Full-width banner for MARGINAL/NULL statuses. Renders nothing for VALIDATED
 *  or when status.banner is empty. */
export function ValidationBanner({ status, testid, className = "" }) {
  if (!status || !status.banner) return null;
  const v = status.verdict;
  const cls = v === "NULL"
    ? "border-slate-500/60 bg-slate-500/10 text-slate-200"
    : v === "MARGINAL"
      ? "border-amber-500/60 bg-amber-500/10 text-amber-100"
      : "border-slate-600 bg-slate-800/50 text-slate-200";
  const Icon = v === "NULL" ? Ban : AlertTriangle;
  return (
    <div
      data-testid={testid || `validation-banner-${status.engine || "unknown"}`}
      data-verdict={v}
      className={`border ${cls} px-3 py-2 flex items-start gap-2 ${className}`}
    >
      <Icon className="h-4 w-4 shrink-0 mt-0.5" />
      <div className="flex-1">
        <div className="mono text-[10px] uppercase tracking-widest opacity-70">
          Validation Status · {status.engine}
        </div>
        <div className="mono text-[11px] mt-0.5 leading-snug">
          {status.banner}
        </div>
        {status.as_of && (
          <div className="mono text-[10px] opacity-60 mt-0.5">As-of {status.as_of}</div>
        )}
      </div>
    </div>
  );
}

/** Small descriptive-only replacement for the intraday "Conf: HIGH" pill.
 *  Explicit intent so users understand it isn't a bug that confidence is gone. */
export function DescriptiveChip({ testid, className = "" }) {
  return (
    <span
      data-testid={testid || "descriptive-chip"}
      className={`inline-flex items-center gap-1 mono text-[10px] uppercase tracking-widest px-1.5 py-0.5 border border-slate-600 bg-slate-800/40 text-slate-400 ${className}`}
      title="This population has no validated predictive edge. Statistic shown is descriptive only."
    >
      <Info className="h-2.5 w-2.5" />
      Descriptive
    </span>
  );
}
